import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { SUBMIT_USER_SURVEY, UPDATE_USER_SETTINGS_AUTH } from '@scio3/graphql-client';
import type { SubmitUserSurveyVariables } from '@scio3/graphql-client';
import { SURVEY_FORM_TYPES } from '@scio3/shared-types';
import type { AppMeta } from '@scio3/shared-types';
import { useAuthStore } from '@/stores/auth-store';
import { useTeamStore } from '@/stores/team-store';
import { getAppIntent, clearAppIntent, rememberConsumedIntent } from '@/lib/app-intent';
import {
  analyticsAppIntentModalOpened,
  analyticsAppIntentPrehookDismissed,
  analyticsAppIntentPrehookShown,
} from '@/lib/analytics-service';
import { useOpenAppModal } from '@/features/assets/hooks/useOpenAppModal';
import {
  ENABLE_ONBOARDING,
  wouldShowOnboardingForm,
} from '@/features/survey/hooks/useShouldShowOnboarding';

// Must match OnboardingForm.tsx (DEFAULT_FORM_ID / FORM_VERSION) so auto-answers land in the
// same dataset as hand-filled surveys.
const FORM_ID = 'account_onboarding';
const FORM_VERSION = 2;

function buildAutoAnswers(
  app: string,
  source: string | undefined,
): SubmitUserSurveyVariables['input']['questions'] {
  return [
    {
      key: 'audience',
      text: 'Who is your primary audience?',
      answer: 'internal',
      answerLabel: 'internal',
    },
    {
      key: 'use_cases',
      text: 'What will you use OptiSigns for?',
      answer: 'dashboards-kpis',
      answerLabel: 'dashboards-kpis',
    },
    { key: 'invite_count', text: 'Team members invited', answer: '0', answerLabel: '0' },
    {
      key: 'app_intent',
      text: 'Dashboard app the visitor came for',
      answer: app,
      answerLabel: app,
    },
    {
      key: 'intent_source',
      text: 'Marketing source of the intent link',
      answer: source ?? 'unknown',
      answerLabel: source ?? 'unknown',
    },
  ];
}

interface ConsumedIntent {
  app: string;
  source?: string;
  intentAgeSeconds?: number;
  surveyAutoSubmitted: boolean;
}

export interface AppIntentLaunchState {
  /** The app to pre-hook, or null when the pre-hook is closed / never opened. */
  prehookApp: AppMeta | null;
  /** "Connect <App>" — close the pre-hook and open the real add-modal. */
  connect: () => void;
  /** "Not now" — close the pre-hook and stop. No modal. */
  dismiss: () => void;
}

/**
 * Consumes a dashboard-app intent cookie exactly once, on the first app-shell load where
 * `me` and `company` are available. Mounted via <AppIntentLauncher /> only when a cookie
 * exists (at AppShell mount, or captured from `/signUp?app=`).
 *
 * For a found app, `app_intent_modal_opened` is emitted from `connect()`, not from this effect:
 * it means "the visitor pressed Connect and the launcher handed off to the add-modal", NOT that
 * the editor rendered — `openAppModal` -> `processApp` is async and gated (shared-folder
 * confirm, per-app creation limit, plan lock), so a blocked launch still emits it. Firing off
 * the smallapp editor store's open transition instead would be exact for manifest apps and
 * silently emit NOTHING for an allowlisted app that routes elsewhere (`isSmallAppEditable` is
 * data-driven off `hasManifest` / `func.name`; the non-smallapp fallback opens a scio1 adapter
 * modal, which this store never sees) — a systematic false negative in place of a rare false
 * positive.
 *
 * State-change ordering is load-bearing: `useShouldShowOnboarding` reads the cookie DURING
 * render, so the auth-store patch has to land before the cookie is cleared — otherwise a
 * re-render landing between the two would mount the survey shell for a frame.
 *
 * The gates inside `openAppModal` still run. The enterprise-only warning is the one that
 * cannot surface here — it is LOCAL state inside `useOpenAppModal`, rendered by
 * AppSelectorDialog, which is not on screen — but no allowlisted app is enterprise-gated
 * (see DASHBOARD_INTENT_APPS), so it cannot fire on this path.
 */
export function useAppIntentLaunch(): AppIntentLaunchState {
  const me = useAuthStore((s) => s.me);
  const company = useAuthStore((s) => s.company);
  const isEmbedded = useAuthStore((s) => s.isEmbedded);
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const { openAppModal } = useOpenAppModal();
  const [submitSurvey] = useMutation<unknown, SubmitUserSurveyVariables>(SUBMIT_USER_SURVEY);
  const [updateSettings] = useMutation(UPDATE_USER_SETTINGS_AUTH);
  const consumedRef = useRef(false);
  const [prehookApp, setPrehookApp] = useState<AppMeta | null>(null);
  /** The consumed intent, captured at consume time so the click emits the same numbers
   *  the pre-hook was shown with (the cookie is long gone by then). */
  const intentRef = useRef<ConsumedIntent | null>(null);

  useEffect(() => {
    if (consumedRef.current) return;
    if (!me || !company) return;
    // No `appMetas.length` wait: ME_QUERY selects `appMetas` inline with `me`, so a set `me`
    // already carries the catalog. An account whose catalog is genuinely EMPTY must still
    // consume the intent (appFound=false, no modal) — waiting for a non-empty array left the
    // cookie in place, and with it the survey suppressed, for the cookie's whole life.
    const appMetas = me.appMetas ?? [];
    const intent = getAppIntent();
    if (!intent) return;

    consumedRef.current = true;
    // Mirror `useShouldShowOnboarding`'s own precedence, and cover BOTH cold-lead signup paths:
    //   - EMAIL signup sets `pendingOnboarding` before navigating (`useSignupFlow`), so that flag
    //     alone answers "was the survey owed?";
    //   - SOCIAL signup (Google / Microsoft) never sets it — it navigates straight to
    //     /app/screenManagement — and arrives with `me.userSettings` null, because open-api
    //     creates that doc lazily, after the `me` read. Hence `treatMissingSettingsAsPending`:
    //     without it neither signal is true, nothing is written, and the survey reappears on the
    //     next load once the tracking upsert has created the doc.
    // Gated on the same kill switch the hook checks first, so an onboarding-disabled env
    // never writes a survey row (and never races that hook's own auto-submit).
    const surveyPending =
      ENABLE_ONBOARDING &&
      (useAuthStore.getState().pendingOnboarding ||
        wouldShowOnboardingForm(
          { me, company, isEmbedded },
          { treatMissingSettingsAsPending: true },
        ));

    // 1. Patch the store FIRST so useShouldShowOnboarding stays false after the cookie is gone.
    if (surveyPending) {
      const currentMe = useAuthStore.getState().me;
      if (currentMe) {
        useAuthStore.getState().setMe({
          ...currentMe,
          userSettings: { ...currentMe.userSettings, onboardFormSubmitted: true },
        });
      }
    }
    // 2. Then the pending flag, which short-circuits the hook to `true` on its own.
    useAuthStore.getState().setPendingOnboarding(false);
    // 3. Only now drop the cookie — the last of the three state changes.
    clearAppIntent();
    // 3b. …but keep WHAT it said for the rest of the tab session, so a screen paired minutes
    //     later can still be attributed to the marketing link (FR-14). Deliberately recorded
    //     here, at consumption, not at "Connect": a visitor who answers "Not now" and pairs a
    //     screen anyway still arrived through this link.
    rememberConsumedIntent(intent.app, intent.source);

    // 4. Best-effort persistence — the user is never blocked on either write.
    if (surveyPending) {
      void submitSurvey({
        variables: {
          input: {
            formId: FORM_ID,
            formVersion: FORM_VERSION,
            formType: SURVEY_FORM_TYPES.ACCOUNT_ONBOARDING,
            questions: buildAutoAnswers(intent.app, intent.source),
          },
        },
      }).catch((error) => {
        console.error('Failed to auto-submit the app-intent onboarding survey:', error);
      });
      void updateSettings({
        variables: { updateData: { onboardFormSubmitted: true }, teamId: activeTeamId },
      }).catch((error) => {
        console.error('Failed to persist onboardFormSubmitted for the app intent:', error);
      });
    }

    const appMeta = appMetas.find((a) => a.appType === intent.app);
    const consumed: ConsumedIntent = {
      app: intent.app,
      source: intent.source,
      intentAgeSeconds:
        intent.at !== undefined
          ? Math.max(0, Math.floor(Date.now() / 1000) - intent.at)
          : undefined,
      surveyAutoSubmitted: surveyPending,
    };
    intentRef.current = consumed;

    // 5. No AppMeta: the dead end is reported on the same event as before, not a new one.
    if (!appMeta) {
      analyticsAppIntentModalOpened({ ...consumed, appFound: false });
      return;
    }
    analyticsAppIntentPrehookShown(consumed);
    setPrehookApp(appMeta);
    // No `openAppModal` dependency: round 3 moved that call out of this effect into `connect`.
  }, [me, company, isEmbedded, activeTeamId, submitSurvey, updateSettings]);

  const connect = useCallback(() => {
    const appMeta = prehookApp;
    const consumed = intentRef.current;
    if (!appMeta || !consumed) return;
    setPrehookApp(null);

    // Tell the editor this open belongs to the intent, so the config dialog can relabel its
    // Close button after a save (FR-12). It rides on the editor entry's own options bag, so it
    // dies with that editor and is invisible to every other open — including a nested
    // "Add new app" child started from inside it. `source` is normalised here because the
    // option's shape is required: the analytics emitters already treat a missing `src` as
    // 'unknown', so this changes no reported value.
    openAppModal(appMeta, {
      appIntent: { app: consumed.app, source: consumed.source ?? 'unknown' },
    });
    // Emitted here, on the hand-off, not on an observed editor open — see the CTA-press note
    // in this hook's docstring for why.
    analyticsAppIntentModalOpened({ ...consumed, appFound: true });
  }, [prehookApp, openAppModal]);

  const dismiss = useCallback(() => {
    const consumed = intentRef.current;
    setPrehookApp(null);
    if (consumed) {
      analyticsAppIntentPrehookDismissed({ app: consumed.app, source: consumed.source });
    }
  }, []);

  return { prehookApp, connect, dismiss };
}
